// src/components/auth/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI, checkPasswordStrength } from '../../services/api';
import { 
  LockClosedIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon 
} from '@heroicons/react/24/outline';
import Footer from '../Footer'; // Add Footer import

function ResetPassword() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [isCheckingStrength, setIsCheckingStrength] = useState(false);
  
  const accountType = searchParams.get('type') || 'user';

  // Set language from institution on mount
  useEffect(() => {
    const institutionData = localStorage.getItem('selectedInstitution');
    if (institutionData) {
      try {
        const institution = JSON.parse(institutionData);
        const publicLanguage = institution.public_portal_language || 'en';
        console.log('[Reset Password] Setting language:', publicLanguage);
        i18n.changeLanguage(publicLanguage).catch(error => {
          console.error('[Reset Password] Failed to set language, falling back to English:', error);
          i18n.changeLanguage('en');
        });
      } catch (error) {
        console.error('[Reset Password] Failed to parse institution data:', error);
        i18n.changeLanguage('en');
      }
    }
  }, [i18n]);

  // Check password strength with debounce
  useEffect(() => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingStrength(true);
      try {
        const response = await checkPasswordStrength(password);
        if (response.success) {
          setPasswordStrength(response.data);
        }
      } catch (error) {
        console.error('Error checking password strength:', error);
      } finally {
        setIsCheckingStrength(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [password]);

  const validatePassword = () => {
    const errors = [];
    
    if (!password || password.length < 10) {
      errors.push(t('auth.resetPassword.validation.minLength'));
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push(t('auth.resetPassword.validation.lowercase'));
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push(t('auth.resetPassword.validation.uppercase'));
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push(t('auth.resetPassword.validation.number'));
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('auth.resetPassword.validation.special'));
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password requirements
    const validationErrors = validatePassword();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. ') + '.');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authAPI.resetPassword(token, password, accountType);
      
      if (response.success) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message || t('auth.resetPassword.errorReset'));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.data?.message || err.message || t('auth.resetPassword.errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong': return 'text-green-600 bg-green-50 border-green-200';
      case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'fair': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'weak': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStrengthBarColor = (strength) => {
    switch (strength) {
      case 'strong': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'fair': return 'bg-yellow-500';
      case 'weak': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const meetsRequirement = (requirement) => {
    if (!password) return false;
    
    switch (requirement) {
      case 'length':
        return password.length >= 10;
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'uppercase':
        return /[A-Z]/.test(password);
      case 'number':
        return /[0-9]/.test(password);
      case 'special':
        return /[!@#$%^&*(),.?":{}|<>]/.test(password);
      default:
        return false;
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-4 py-8">
          <div className="w-full max-w-md text-center">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
              <div className="rounded-full bg-green-100 p-3 mx-auto w-fit mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('auth.resetPassword.successTitle')}</h2>
              <p className="text-slate-600">
                {t('auth.resetPassword.successMessage')}
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">{t('auth.resetPassword.title')}</h1>
              <p className="text-slate-600">{t('auth.resetPassword.subtitle')}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.resetPassword.newPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('auth.resetPassword.passwordPlaceholder')}
                    required
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                
                {/* Password Requirements */}
                {password && (
                  <div className="mt-3 space-y-2">
                    {/* Strength Bar */}
                    {passwordStrength && !isCheckingStrength && (
                      <>
                        <div className="flex gap-1">
                          {[25, 50, 75, 100].map((threshold) => (
                            <div
                              key={threshold}
                              className={`h-1.5 flex-1 rounded-full transition-colors ${
                                passwordStrength.score >= threshold 
                                  ? getStrengthBarColor(passwordStrength.strength)
                                  : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        
                        <div className={`text-xs font-medium px-2 py-1 rounded border inline-block ${
                          getStrengthColor(passwordStrength.strength)
                        }`}>
                          {t(`auth.resetPassword.strength.${passwordStrength.strength}`)}
                        </div>
                      </>
                    )}

                    {/* Requirements Checklist */}
                    <div className="space-y-1 text-xs">
                      <div className={`flex items-center ${
                        meetsRequirement('length') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('length') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.resetPassword.requirements.minLength')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('lowercase') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('lowercase') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.resetPassword.requirements.lowercase')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('uppercase') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('uppercase') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.resetPassword.requirements.uppercase')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('number') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('number') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.resetPassword.requirements.number')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('special') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('special') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.resetPassword.requirements.special')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.resetPassword.confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                    required
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                
                {confirmPassword && (
                  <div className={`mt-1 text-xs flex items-center ${
                    password === confirmPassword ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {password === confirmPassword ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {t('auth.resetPassword.passwordsMatch')}
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        {t('auth.resetPassword.passwordsNoMatch')}
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('auth.resetPassword.resetting') : t('auth.resetPassword.submitButton')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <a href="/login" className="text-sm text-cyan-600 hover:text-cyan-700">
                {t('auth.resetPassword.backToLogin')}
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default ResetPassword;